// Keeps the first `count` columns of a table in place while the rest
// scrolls sideways. Each frozen column needs to know how far from the left
// it sits, which depends on the real widths of the columns before it (and
// on which columns are switched on), so this measures the header cells and
// writes the offsets as CSS variables on the table: --fz-1 … --fz-5. The
// table also gets data-freeze="N"; the `.table-freeze-cols` rules in
// index.css do the rest (sticky cells, solid background, a soft shadow on
// the last frozen column).
//
// On a narrow screen (wrapper under 640px wide) `mobileCount` is used
// instead, so frozen columns never cover a phone screen.

import { useEffect, type RefObject } from 'react';

export const MAX_FROZEN = 5;

export function useFrozenColumns(
  tableRef: RefObject<HTMLTableElement | null>,
  count: number,
  mobileCount = 0,
  deps: unknown[] = [],
) {
  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    const measure = () => {
      const wrapWidth = table.parentElement?.clientWidth ?? 9999;
      const n = Math.min(MAX_FROZEN, wrapWidth < 640 ? mobileCount : count);
      const ths = Array.from(table.tHead?.rows[0]?.cells ?? []);
      let left = 0;
      for (let i = 0; i < n && i < ths.length; i++) {
        table.style.setProperty(`--fz-${i + 1}`, `${left}px`);
        left += ths[i].getBoundingClientRect().width;
      }
      if (n > 0) table.dataset.freeze = String(n);
      else delete table.dataset.freeze;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(table);
    if (table.parentElement) ro.observe(table.parentElement);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRef, count, mobileCount, ...deps]);
}
